/**
 * procurementPermissions.ts — Issue #331.
 *
 * Service catalog / supplier / purchase order permission constants and
 * helpers. Mirrors backend @RequirePermission('service_catalog', '...'),
 * @RequirePermission('supplier', '...'), and
 * @RequirePermission('purchase_order', '...') decorators.
 *
 * Design principles mirror financePermissions.ts: no React imports, pure
 * TypeScript so the helpers stay test-friendly.
 */
import { hasPermission, hasAnyPermission } from './crmPermissions';

export { hasPermission, hasAnyPermission };

export const SERVICE_CATALOG_PERMISSIONS = {
  READ: 'service_catalog:read',
  CREATE: 'service_catalog:create',
  UPDATE: 'service_catalog:update',
  REVIEW: 'service_catalog:review',
} as const;

export const SUPPLIER_PERMISSIONS = {
  READ: 'supplier:read',
  CREATE: 'supplier:create',
  UPDATE: 'supplier:update',
} as const;

export const PURCHASE_ORDER_PERMISSIONS = {
  READ: 'purchase_order:read',
  CREATE: 'purchase_order:create',
  UPDATE: 'purchase_order:update',
  APPROVE: 'purchase_order:approve',
  RECEIVE: 'purchase_order:receive',
} as const;

export type ServiceCatalogPermission =
  (typeof SERVICE_CATALOG_PERMISSIONS)[keyof typeof SERVICE_CATALOG_PERMISSIONS];
export type SupplierPermission =
  (typeof SUPPLIER_PERMISSIONS)[keyof typeof SUPPLIER_PERMISSIONS];
export type PurchaseOrderPermission =
  (typeof PURCHASE_ORDER_PERMISSIONS)[keyof typeof PURCHASE_ORDER_PERMISSIONS];

export function canSeeServiceCatalogMenu(
  granted: readonly string[],
): boolean {
  return hasPermission(granted, SERVICE_CATALOG_PERMISSIONS.READ);
}

export function canSeeSupplierMenu(granted: readonly string[]): boolean {
  return hasPermission(granted, SUPPLIER_PERMISSIONS.READ);
}

export function canSeePurchaseOrderMenu(granted: readonly string[]): boolean {
  return hasPermission(granted, PURCHASE_ORDER_PERMISSIONS.READ);
}

export function canSeeProcurementMenu(granted: readonly string[]): boolean {
  return hasAnyPermission(granted, [
    SERVICE_CATALOG_PERMISSIONS.READ,
    SUPPLIER_PERMISSIONS.READ,
    PURCHASE_ORDER_PERMISSIONS.READ,
  ]);
}
